import { AlertTriangle, ChevronRight, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import Button from "../../../../../shared/ui/button/Button";

interface SessionReviewBannerProps {
  /** Ask for the AI analysis: the session is completed with it. */
  onAnalyze: () => void;
  /** Finish the session as it is: nothing is sent to a model. */
  onFinishWithoutAnalysis: () => void;
  analyzing?: boolean;
  finishing?: boolean;
  /** AI Insights not adopted: only the "finish as it is" answer is offered. */
  canAnalyze?: boolean;
  error?: string | null;
}

/**
 * The question a reviewed session asks its therapist.
 *
 * The analysis no longer follows the notes on its own — the scribe can mishear
 * a name or invent an agreement, and an analysis built on that inherits it. So
 * the notes are read by a person, and the decision that follows is theirs:
 * run the analysis, or finish the session with the notes as they are.
 */
export default function SessionReviewBanner({
  onAnalyze,
  onFinishWithoutAnalysis,
  analyzing = false,
  finishing = false,
  canAnalyze = true,
  error = null,
}: SessionReviewBannerProps) {
  const { t } = useTranslation();

  return (
    <motion.div
      role="status"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl border border-amber-200 bg-amber-50/60 p-6 shadow-theme-sm dark:border-amber-800/40 dark:bg-amber-900/10"
    >
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100 shadow-sm dark:bg-amber-500/10">
          <FileText aria-hidden="true" className="h-6 w-6 text-amber-600 dark:text-amber-400" />
        </div>

        <div className="min-w-[16rem] flex-1">
          <h3 className="mb-1 text-lg font-bold text-content-primary">
            {t("session.review.title", "Notes ready to review")}
          </h3>
          <p className="max-w-2xl leading-relaxed text-content-tertiary">
            {canAnalyze
              ? t("session.review.description")
              : t("session.review.descriptionWithoutAi")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 self-center">
          {canAnalyze && (
            <Button
              onClick={onAnalyze}
              loading={analyzing}
              disabled={finishing}
              endIcon={<ChevronRight className="size-4" />}
            >
              {t("session.review.runAnalysis", "Run the analysis")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onFinishWithoutAnalysis}
            loading={finishing}
            disabled={analyzing}
          >
            {t("session.review.finishWithoutAnalysis", "Finish without analysis")}
          </Button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="mt-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50/60 p-4 dark:border-red-800/40 dark:bg-red-900/10"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 flex-shrink-0 text-red-500" />
          <p className="text-sm leading-relaxed text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}
    </motion.div>
  );
}
